import { OwnershipLabelsProvider } from "../kyc/OwnershipLabels";
// src/pages/KycViewPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Toast } from "primereact/toast";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

import { clientToKycCustomer } from "../utils/syncClientKyc";

import KycViewCustomerDetails from "../kyc/sections/view/KycViewCustomerDetails";
import KycViewOwnership from "../kyc/sections/view/KycViewOwnership";
import KycViewSignatories from "../kyc/sections/view/KycViewSignatories";
import KycViewSowSof from "../kyc/sections/view/KycViewSowSof";

import KycViewSanctions from "../kyc/sections/view/KycViewSanctions";
import KycViewPep from "../kyc/sections/view/KycViewPep";
import KycViewAdverseMedia from "../kyc/sections/view/KycViewAdverseMedia";
import KycViewRiskAssessment from "../kyc/sections/view/KycViewRiskAssessment";

import KycSectionDocuments from "../kyc/sections/common/KycSectionDocuments";
import KycSectionFinalOverview from "../kyc/sections/common/KycSectionFinalOverview";
import AdverseMediaTopNav from "../components/ui/AdverseMediaTopNav";

import iconHomeA from "../images/home-A.svg";
import { useStaffTopBand } from "../components/ui/StaffTopBandContext";
import { useAuth } from "../auth/AuthProvider";
import { forceReleaseBodyScrollLock } from "../kyc/sections/common/common_utils/useLockBodyScroll";
import {
  DEFAULT_RELATIONSHIP_RISK_SETTINGS,
  RELATIONSHIP_RISK_DEFAULT_DOC_ID,
  RELATIONSHIP_RISK_SETTINGS_COLLECTION,
  sanitizeRelationshipRiskSettings,
} from "../utils/relationshipRiskRules";
import "./css/KycFormPage.css";
import "./css/KycViewPage.css";

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

function normalizeClientType(raw) {
  const v = String(raw || "")
    .trim()
    .toLowerCase();
  if (v === "entity" || v === "company") return "entity";
  if (v === "individual" || v === "person") return "individual";
  return "";
}

function countryRiskUsesMainCountryOfOperation(orgData, defaultData) {
  if (typeof orgData?.useMainCountryOfOperation === "boolean") {
    return orgData.useMainCountryOfOperation;
  }
  return defaultData?.useMainCountryOfOperation === true;
}

function effectiveRelationshipRiskSettings(orgData, defaultData) {
  const defaultSettings = sanitizeRelationshipRiskSettings(defaultData?.rules || defaultData);
  if (!orgData || orgData.useGeneralSettings === true) return defaultSettings;
  const orgSettings = sanitizeRelationshipRiskSettings(orgData.rules || orgData);
  return orgData.useGeneralModel === true
    ? {
        ...orgSettings,
        activeModel: defaultSettings.activeModel,
      }
    : orgSettings;
}

function typeLabel(v) {
  if (v === "entity") return "Entity";
  if (v === "individual") return "Individual";
  return "";
}

function toDateOrNull(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function KycViewPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);
  const { isAdmin, me, role, authLoading } = useAuth();
  const myOrgId = String(me?.orgId || "").trim();
  const myTeamId = String(me?.teamId || "").trim();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [orgJurisdiction, setOrgJurisdiction] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientType, setClientType] = useState("");
  const [useMainCountryOfOperation, setUseMainCountryOfOperation] = useState(false);
  const [relationshipRiskSettings, setRelationshipRiskSettings] = useState(() =>
    sanitizeRelationshipRiskSettings(DEFAULT_RELATIONSHIP_RISK_SETTINGS)
  );

  const [form, setForm] = useState({
    customer: {
      type: "individual",
      name: "",
      nationalityOrIncorporationCountry: "",
      mainCountryOfOperation: "",
      dobOrIncorporationDate: null,
      email: "",
      address: "",
      city: "",
      country: "",
      website: "",
      businessActivity: "",
    },
    ownership: {
      shareholders: [],
      ubos: [],
      controllers: [],
      complexStructure: false,
    },
    sourceOfFundsWealth: {
      sourceOfFunds: "",
      sourceOfFundsDetails: "",
      sourceOfWealth: [],
      sourceOfWealthDetails: "",
      netWorthCurrency: "USD",
      netWorthRange: "",
      annualIncomeCurrency: "USD",
      annualIncomeRange: "",
      annualTurnoverCurrency: "USD",
      annualTurnoverRange: "",
      sourceOfFundsTypes: [],
      thirdPartyDetails: "",
      sourceOfFundsCashDetails: "",
      sourceOfFundsOtherDetails: "",
    },
    sanctions: {
      clientDeclared: false,
      clientDetails: "",
      staffDecision: null,
      staffAssessment: "",
    },
    pep: {
      clientDeclared: false,
      clientDetails: "",
      staffDecision: null,
      staffAssessment: "",
    },
    adverseMedia: {
      clientDeclared: false,
      clientDetails: "",
      staffDecision: null,
      staffAssessment: "",
      language: "en",
    },
    internalUse: {
      sanctionsScreening: null,
      pepScreening: null,
      adverseMediaScreening: null,
    },
    finalOverview: {
      calculatedRiskRating: "",
      eddRequired: null,
      sarStatus: "no",
    },
    documentsChecklist: {
      parties: {},
      sow: {},
    },
  });

  useEffect(() => {
    forceReleaseBodyScrollLock();
  }, []);

  const customerType = form?.customer?.type || clientType || "individual";
  const isEntity = customerType === "entity";

  async function handleLogout() {
    sessionStorage.setItem("mykyc:loggedOut", "1");
    await signOut(auth);
    nav("/login", { replace: true });
  }

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setErr("");
        setLoading(true);

        const clientSnap = await getDoc(doc(db, "clients", id));
        if (!alive) return;
        if (!clientSnap.exists()) throw new Error("Client not found.");

        const c = clientSnap.data();
        const cOrgId = String(c?.orgId || "").trim();
        const cAccessScope = String(c?.accessScope || "").trim();
        const cOwnerUid = String(c?.ownerUid || "").trim();
        const cTeamId = String(c?.teamId || "").trim();
        const myUid = String(auth.currentUser?.uid || "").trim();

        if (!isAdmin && cOrgId && cOrgId !== myOrgId) {
          throw new Error("Access denied: this client belongs to another organisation.");
        }
        if (
          !isAdmin &&
          role !== "manager" &&
          !(
            (cAccessScope === "team" && myTeamId && cTeamId === myTeamId) ||
            (cAccessScope === "individual" && cOwnerUid === myUid)
          )
        ) {
          throw new Error("Access denied: this client belongs to another staff member.");
        }

        const name = c?.clientName || c?.name || "";
        const typeRaw = c?.type || c?.clientType || c?.customerType || "";
        const normalizedType = normalizeClientType(typeRaw);

        setClientName(name);
        setClientType(normalizedType);
        setOrgJurisdiction(null);
        setUseMainCountryOfOperation(false);
        setRelationshipRiskSettings(sanitizeRelationshipRiskSettings(DEFAULT_RELATIONSHIP_RISK_SETTINGS));

        if (cOrgId) {
          const [
            orgSnap,
            countryDefaultSnap,
            countryOrgSnap,
            relationshipDefaultSnap,
            relationshipOrgSnap,
          ] = await Promise.all([
            getDoc(doc(db, "orgs", cOrgId)),
            getDoc(doc(db, "countryRisk", "lists")),
            getDoc(doc(db, "countryRisk", cOrgId)),
            getDoc(doc(db, RELATIONSHIP_RISK_SETTINGS_COLLECTION, RELATIONSHIP_RISK_DEFAULT_DOC_ID)),
            getDoc(doc(db, RELATIONSHIP_RISK_SETTINGS_COLLECTION, cOrgId)),
          ]);
          if (!alive) return;
          setOrgJurisdiction(orgSnap.exists() ? (orgSnap.data()?.geo || "uae") : null);
          setUseMainCountryOfOperation(
            countryRiskUsesMainCountryOfOperation(
              countryOrgSnap.exists() ? countryOrgSnap.data() : {},
              countryDefaultSnap.exists() ? countryDefaultSnap.data() : {}
            )
          );
          setRelationshipRiskSettings(
            effectiveRelationshipRiskSettings(
              relationshipOrgSnap.exists() ? relationshipOrgSnap.data() : null,
              relationshipDefaultSnap.exists() ? relationshipDefaultSnap.data() : null
            )
          );
        }

        setForm((prev) => ({
          ...prev,
          customer: {
            ...prev.customer,
            ...clientToKycCustomer(c),
            type: normalizedType || prev.customer.type,
          },
        }));

        const kycSnap = await getDoc(doc(db, "kyc", id));
        if (!alive) return;

        if (kycSnap.exists()) {
          const data = kycSnap.data();
          // const { documents, ...dataClean } = data;
          const dataClean = data;

          setForm((prev) => ({
            ...prev,
            ...dataClean,

            customer: {
              ...prev.customer,
              ...(dataClean.customer || {}),
              type:
                normalizeClientType(dataClean.customer?.type) ||
                prev.customer.type,
              dobOrIncorporationDate: toDateOrNull(
                dataClean.customer?.dobOrIncorporationDate
              ),
            },

            ownership: { ...prev.ownership, ...(dataClean.ownership || {}) },

            sourceOfFundsWealth: {
              ...prev.sourceOfFundsWealth,
              ...(dataClean.sourceOfFundsWealth || {}),
              sourceOfWealth: Array.isArray(
                dataClean.sourceOfFundsWealth?.sourceOfWealth
              )
                ? dataClean.sourceOfFundsWealth.sourceOfWealth
                : [],
              sourceOfFundsTypes: Array.isArray(
                dataClean.sourceOfFundsWealth?.sourceOfFundsTypes
              )
                ? dataClean.sourceOfFundsWealth.sourceOfFundsTypes
                : [],
            },

            sanctions: { ...prev.sanctions, ...(dataClean.sanctions || {}) },
            pep: { ...prev.pep, ...(dataClean.pep || {}) },
            adverseMedia: {
              ...prev.adverseMedia,
              ...(dataClean.adverseMedia || {}),
            },

            internalUse: {
              ...prev.internalUse,
              ...(dataClean.internalUse || {}),
            },
            finalOverview: {
              ...prev.finalOverview,
              ...(dataClean.finalOverview || {}),
            },
          }));
        }
      } catch (e) {
        setErr(e?.message || "Failed to load KYC view.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    if (!authLoading) load();
    return () => {
      alive = false;
    };
  }, [id, authLoading, isAdmin, myOrgId, myTeamId, role]);

  useEffect(() => {
    const t = location.state?.toast;
    const toastId = location.state?.toastId;
    if (!t || !toastId) return;

    const key = `toast_consumed_${toastId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");

    toastRef.current?.show(t);
    nav(".", { replace: true, state: null });
  }, [location.state, nav]);

  const sectionNo = useMemo(() => {
    let n = 1;
    return {
      customer: n++,
      ownership: isEntity ? n++ : null,
      signatories: isEntity ? n++ : null,
      sow: n++,
      risk: n++,
      pep: n++,
      sanctions: n++,
      adverse: n++,
      documents: n++,
      finalOverview: n++,
    };
  }, [isEntity]);

  const KYC_NAV_ITEMS = useMemo(() => {
    const pad2 = (n) => String(n || "").padStart(2, "0");
    const items = [
      {
        id: "kyc-sec-customer",
        num: pad2(sectionNo.customer),
        label: "Customer",
      },
    ];

    if (isEntity && sectionNo.ownership) {
      items.push({
        id: "kyc-sec-ownership",
        num: pad2(sectionNo.ownership),
        label: "Ownership",
      });
    }

    if (isEntity && sectionNo.signatories) {
      items.push({
        id: "kyc-sec-signatories",
        num: pad2(sectionNo.signatories),
        label: "Signatories",
      });
    }

    items.push(
      { id: "kyc-sec-sow", num: pad2(sectionNo.sow), label: "SoW / SoF" },
      { id: "kyc-sec-risk", num: pad2(sectionNo.risk), label: "Risk" },
      { id: "kyc-sec-pep", num: pad2(sectionNo.pep), label: "PEP" },
      {
        id: "kyc-sec-sanctions",
        num: pad2(sectionNo.sanctions),
        label: "Sanctions",
      },
      {
        id: "kyc-sec-adverse",
        num: pad2(sectionNo.adverse),
        label: "Adverse media",
      },
      {
        id: "kyc-sec-docs",
        num: pad2(sectionNo.documents),
        label: "Documents",
      },
      {
        id: "kyc-sec-final",
        num: pad2(sectionNo.finalOverview),
        label: "Final",
      }
    );

    return items;
  }, [sectionNo, isEntity]);

  const [activeKycSectionId, setActiveKycSectionId] =
    useState("kyc-sec-customer");

  useEffect(() => {
    const items = KYC_NAV_ITEMS || [];
    const ids = items.map((x) => x.id).filter(Boolean);

    if (!ids.length) return;

    const TOP_OFFSET = 130;
    let raf = 0;

    const computeActive = () => {
      const line = TOP_OFFSET;
      let bestId = ids[0];
      let bestTop = -Infinity;

      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        const top = rect.top;

        if (top <= line && top > bestTop) {
          bestTop = top;
          bestId = id;
        }
      }

      setActiveKycSectionId(bestId);
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(computeActive);
    };

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    onScrollOrResize();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [KYC_NAV_ITEMS]);

  useStaffTopBand(
    {
      title: (
        <motion.span
          key="kyc-view-title"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: "easeInOut" }}
        >
          KYC · View
        </motion.span>
      ),
      contentClassName: "kycPartClientInfo",
      smallBandColor: "#8fa8bb",
      titleColor: "#ffffff",
      right: (
        <div className="d-flex align-items-center gap-2 kycTitleActions">
          {clientName ? (
            <div className="kycTitleClientBlock">
              <span className="kycTitleClientName">{clientName}</span>
              {clientType || form?.customer?.type ? (
                <span
                  className={
                    "kycTypeBadge " +
                    ((clientType || form?.customer?.type) === "entity"
                      ? "kycTypeBadgeEntity"
                      : "kycTypeBadgeIndividual")
                  }
                >
                  {typeLabel(clientType || form?.customer?.type)}
                </span>
              ) : null}
            </div>
          ) : null}

          <button
            type="button"
            className="btn btn-sm btn-outline-light kycInboxBackBtn"
            onClick={() => nav("/clients")}
            style={{ whiteSpace: "nowrap" }}
            aria-label="Back to clients"
            title="Back to clients"
          >
            <img src={iconHomeA} alt="" className="kycInboxBackHomeIcon" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-light kycInboxLogoutBtn"
            onClick={handleLogout}
            style={{ whiteSpace: "nowrap" }}
          >
            Log out
          </button>
        </div>
      ),
    },
    [clientName, clientType, form?.customer?.type, nav]
  );

  return (
    <OwnershipLabelsProvider jurisdiction={orgJurisdiction}>
    <div className="kycFormPage kycViewMode staffShellContentPage">
      <AdverseMediaTopNav
        items={KYC_NAV_ITEMS}
        activeId={activeKycSectionId}
        offset={95}
      />

      <motion.div
        className="container pb-4 kycPartClientInfo"
        variants={pageAnim}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.25 }}
      >
        <div className="kycTopActions">
          <div className="kycMeta">View-only condensed document</div>

          <div className="d-flex gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-sm kycBtnOutline"
              disabled
              title="PDF generation is temporarily disabled"
            >
              Generate PDF
            </button>

            <button
              type="button"
              className="btn btn-sm kycBtnOutline"
              onClick={() => nav(`/kyc/${id}`, { replace: true })}
            >
              <i className="bi bi-pencil-square me-2" />
              Edit
            </button>
          </div>
        </div>

        {loading ? (
          <div className="card kycCard p-4 text-muted">Loading…</div>
        ) : null}
        {err ? <div className="alert alert-danger">{err}</div> : null}

        {!loading && !err ? (
          <>
            {/* ✅ LARGE GROUP CARD 1 (same as form): Client information */}
            <div className="kycGroup kycGroupInfo">
              <div className="kycGroupHead">
                <div className="kycGroupTitle">Client information</div>
                <div className="kycGroupHint">
                  Identity, ownership & financial profile
                </div>
              </div>

              <div className="d-grid" style={{ gap: 14 }}>
                <section id="kyc-sec-customer">
                  <KycViewCustomerDetails
                    form={form}
                    sectionNo={sectionNo.customer}
                    useMainCountryOfOperation={useMainCountryOfOperation}
                  />
                </section>
                {isEntity ? (
                  <section id="kyc-sec-ownership">
                    <KycViewOwnership
                      form={form}
                      sectionNo={sectionNo.ownership}
                    />
                  </section>
                ) : null}
                {isEntity ? (
                  <section id="kyc-sec-signatories">
                    <KycViewSignatories
                      form={form}
                      sectionNo={sectionNo.signatories}
                    />
                  </section>
                ) : null}
                <section id="kyc-sec-sow">
                  <KycViewSowSof form={form} sectionNo={sectionNo.sow} />
                </section>
              </div>
            </div>

            {/* ✅ LARGE GROUP CARD 2 (same as form): Compliance assessment */}
            <div className="kycGroup kycGroupAssess">
              <div className="kycGroupHead">
                <div className="kycGroupTitle">Compliance assessment</div>
                <div className="kycGroupHint">
                  Sanctions, PEP & adverse media
                </div>
              </div>

              <div className="d-grid" style={{ gap: 14 }}>
                <section id="kyc-sec-risk">
                  <div style={{ marginBottom: 15, marginTop: 5 }}>
                    <KycViewRiskAssessment
                      form={form}
                      sectionNo={sectionNo.risk}
                    />
                  </div>
                </section>
                <section id="kyc-sec-pep">
                  <KycViewPep form={form} sectionNo={sectionNo.pep} />
                </section>
                <section id="kyc-sec-sanctions">
                  <KycViewSanctions
                    form={form}
                    sectionNo={sectionNo.sanctions}
                  />
                </section>
                <section id="kyc-sec-adverse">
                  <KycViewAdverseMedia
                    form={form}
                    sectionNo={sectionNo.adverse}
                  />
                </section>
              </div>
            </div>
            {/* ------ */}
            <div className="kycGroup kycGroupAssess">
              <div className="kycGroupHead">
                <div className="kycGroupTitle">Documents</div>
                <div className="kycGroupHint">
                  Supporting documents checklist
                </div>
              </div>

              <div className="d-grid" style={{ gap: 14 }}>
                <section id="kyc-sec-docs">
                  <KycSectionDocuments
                    form={form}
                    sectionNo={sectionNo.documents}
                    readOnly={true}
                    clientId={id}
                    relationshipRiskSettings={relationshipRiskSettings}
                  />
                </section>
              </div>
            </div>
            {/* ------ */}
            <div className="kycGroup kycGroupAssess">
              <div className="kycGroupHead">
                <div className="kycGroupTitle">Final overview</div>
                <div className="kycGroupHint">
                  Final decision and compliance summary
                </div>
              </div>

              <div className="d-grid" style={{ gap: 14 }}>
                <section id="kyc-sec-final">
                  <KycSectionFinalOverview
                    form={form}
                    sectionNo={sectionNo.finalOverview}
                    readOnly={true}
                    clientId={id}
                    relationshipRiskSettings={relationshipRiskSettings}
                  />
                </section>
              </div>
            </div>
            {/* ------ */}
          </>
        ) : null}
      </motion.div>

      <Toast ref={toastRef} baseZIndex={6000} />
    </div>
    </OwnershipLabelsProvider>
  );
}
